<template>
  <div class="modal-overlay" @click="handleOverlayClick">
    <div class="modal-container">
      <div class="modal-content">
        <!-- Loading Animation -->
        <div class="loading-container">
          <div class="loading-spinner">
            <div class="spinner-ring"></div>
            <div class="spinner-ring"></div>
            <div class="spinner-ring"></div>
          </div>
          <div class="aws-logo">🔍</div>
        </div>
        
        <!-- Content -->
        <div class="content">
          <h2>Processing your image</h2>
          <p class="main-message">
            We're using AWS Rekognition to analyze your uploaded image for quality and content validation.
          </p>
          
          <div class="progress-steps">
            <div class="step" :class="{ active: currentStep >= 1, completed: currentStep > 1 }">
              <div class="step-indicator">
                <span v-if="currentStep > 1">✓</span>
                <span v-else>1</span>
              </div>
              <span class="step-text">Upload Complete</span>
            </div>
            
            <div class="step-connector" :class="{ active: currentStep >= 2 }"></div>
            
            <div class="step" :class="{ active: currentStep >= 2, completed: currentStep > 2 }">
              <div class="step-indicator">
                <span v-if="currentStep > 2">✓</span>
                <span v-else>2</span>
              </div>
              <span class="step-text">AI Analysis</span>
            </div>
            
            <div class="step-connector" :class="{ active: currentStep >= 3 }"></div>
            
            <div class="step" :class="{ active: currentStep >= 3, completed: currentStep > 3 }">
              <div class="step-indicator">
                <span v-if="currentStep > 3">✓</span>
                <span v-else>3</span>
              </div>
              <span class="step-text">Validation</span>
            </div>
          </div>
          
          <div class="processing-details">
            <div class="detail-item">
              <span class="icon">🔒</span>
              <span>Checking image safety and appropriateness</span>
            </div>
            <div class="detail-item">
              <span class="icon">✨</span>
              <span>Analyzing image quality and clarity</span>
            </div>
            <div class="detail-item">
              <span class="icon">🎯</span>
              <span>Optimizing for campaign performance</span>
            </div>
          </div>
          
          <p class="estimated-time">
            <strong>Estimated time:</strong> {{ estimatedTimeRemaining }} seconds
          </p>
          
          <div class="info-note">
            <span class="info-icon">ℹ️</span>
            <p>This process ensures your image meets our quality standards and complies with platform guidelines.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onUnmounted } from 'vue'

export default defineComponent({
  name: 'HoldingModal',
  emits: ['close', 'processing-complete'],
  setup(_, { emit }) {
    const currentStep = ref(1)
    const estimatedTimeRemaining = ref(3)
    let progressInterval: number | null = null
    let timeInterval: number | null = null
    
    const startProcessing = () => {
      // Step progression
      progressInterval = window.setInterval(() => {
        if (currentStep.value < 3) {
          currentStep.value++
        }
      }, 1000)
      
      // Time countdown
      timeInterval = window.setInterval(() => {
        if (estimatedTimeRemaining.value > 0) {
          estimatedTimeRemaining.value--
        } else {
          // Processing complete
          currentStep.value = 4
          setTimeout(() => {
            emit('processing-complete')
            emit('close')
          }, 500)
        }
      }, 1000)
    }
    
    const handleOverlayClick = (event: MouseEvent) => {
      // Don't close modal when clicking overlay during processing
      // This ensures users don't accidentally interrupt the process
      event.stopPropagation()
    }
    
    onMounted(() => {
      startProcessing()
    })
    
    onUnmounted(() => {
      if (progressInterval) {
        clearInterval(progressInterval)
      }
      if (timeInterval) {
        clearInterval(timeInterval)
      }
    })
    
    return {
      currentStep,
      estimatedTimeRemaining,
      handleOverlayClick
    }
  }
})
</script>

<style scoped>
.modal-overlay {
  animation: modalFadeIn 0.3s ease-out;
}

@keyframes modalFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.loading-container {
  position: relative;
  margin-bottom: 1.875rem;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.info-note p {
  margin: 0;
  color: var(--color-text);
  font-size: 0.875rem;
  line-height: 1.5;
  text-align: left;
}

@media (max-width: 37.5rem) {
  .progress-steps {
    flex-direction: column;
    gap: 0.9375rem;
  }

  .step {
    flex-direction: row;
    width: 100%;
    justify-content: flex-start;
    text-align: left;
  }

  .step-connector {
    display: none;
  }

  .processing-details {
    gap: 0.625rem;
  }
}
</style>
